import { DownloadIcon, ShareIcon } from "lucide-react";
import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@stackje/shared-components/ui/dialog";
import { Button } from "@stackje/shared-components/ui/button";

// Add type for BeforeInstallPromptEvent
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

// Add type for window with PWA prompt
interface WindowWithPWA extends Window {
  __pwaPrompt?: BeforeInstallPromptEvent;
}

export function InstallPwaBanner() {
  const [deferredPrompt, setDeferredPrompt] =
    useState<BeforeInstallPromptEvent | null>(null);
  const [isStandalone, setIsStandalone] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [showIOSDialog, setShowIOSDialog] = useState(false);

  // Initial setup effect - runs once
  useEffect(() => {
    const initializePWAChecks = () => {
      const checkInstallationStatus = () => {
        const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

        if (!isMobile) {
          setIsIOS(false);
          setDeferredPrompt(null);
          return;
        }

        const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent);
        setIsIOS(isIOSDevice);

        const isInStandaloneMode =
          window.matchMedia("(display-mode: standalone)").matches ||
          (window.navigator as { standalone?: boolean }).standalone ||
          document.referrer.includes("android-app://");

        setIsStandalone(isInStandaloneMode);

        // Check if we already have a stored prompt
        const windowWithPWA = window as WindowWithPWA;
        const storedPrompt = windowWithPWA.__pwaPrompt;
        if (storedPrompt && !isIOSDevice) {
          setDeferredPrompt(storedPrompt);
        }
      };

      checkInstallationStatus();
    };

    // Set up the beforeinstallprompt handler immediately
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      // Store the event for later use
      const windowWithPWA = window as WindowWithPWA;
      windowWithPWA.__pwaPrompt = e as BeforeInstallPromptEvent;
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);

    // Then initialize the rest
    if (document.readyState === "complete") {
      initializePWAChecks();
    } else {
      window.addEventListener("load", initializePWAChecks);
    }

    return () => {
      window.removeEventListener(
        "beforeinstallprompt",
        handleBeforeInstallPrompt,
      );
      window.removeEventListener("load", initializePWAChecks);
    };
  }, []);

  // Only show install button if:
  // 1. On iOS mobile and not in standalone mode, OR
  // 2. Have a deferred prompt (Android)
  // 3. Service worker is available
  const shouldShowInstallBanner =
    "serviceWorker" in navigator &&
    ((!isStandalone && isIOS) || deferredPrompt !== null);

  if (!shouldShowInstallBanner) return null;

  const handleInstallClick = async () => {
    if (isIOS) {
      setShowIOSDialog(true);
      return;
    }

    if (!deferredPrompt) return;

    // Show the install prompt
    await deferredPrompt.prompt();

    // Wait for the user to respond to the prompt
    const { outcome } = await deferredPrompt.userChoice;

    if (outcome === "accepted") {
      console.log("User accepted the install prompt");
    } else {
      console.log("User dismissed the install prompt");
    }

    // Clear the deferredPrompt
    setDeferredPrompt(null);
  };

  return (
    <>
      <div className="w-full border-b border-brand/10">
        <div className="max-w-3xl mx-auto px-4">
          <div className="bg-brand/5 rounded-lg p-4 flex items-center justify-between gap-4 my-4">
            <div className="flex items-center gap-3">
              <div className="text-left">
                <h2 className="font-semibold text-brand">Install Stack.je</h2>
                <p className="text-sm text-brand/80">
                  Manage your site with an app for a better experience.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                className="bg-brand hover:bg-brand/90 text-white whitespace-nowrap"
                onClick={handleInstallClick}
              >
                <DownloadIcon className="h-4 w-4 mr-1" />
                Install
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* iOS Installation Instructions Dialog */}
      <Dialog open={showIOSDialog} onOpenChange={setShowIOSDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-brand">
              Install Stack.je on iOS
            </DialogTitle>
            <DialogDescription className="space-y-4 text-left pt-4">
              <p>Follow these steps to install Stack.je on your iOS device:</p>
              <ol className="list-decimal list-inside space-y-2">
                <li>
                  Tap the{" "}
                  <span className="inline-flex items-center px-2 py-1 bg-brand/5 rounded">
                    Share <ShareIcon className="h-4 w-4 ml-1" />
                  </span>{" "}
                  button
                </li>
                <li>Scroll down and tap &ldquo;Add to Home Screen&rdquo;</li>
                <li>Tap &ldquo;Add&rdquo; in the top right corner</li>
              </ol>
              <p className="text-sm text-gray-500 mt-4">
                Once installed, you&apos;ll be able to access Stack.je directly
                from your home screen!
              </p>
            </DialogDescription>
          </DialogHeader>
        </DialogContent>
      </Dialog>
    </>
  );
}
